import { useEffect, useState } from "react";
import { useGlobalViewMode } from "../components/ViewModeToggle";

import { PageHeader } from "../components/GradientCard";import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { fmtDateStr } from "../lib/datetime";
import {

  KeyRound, Eye, EyeOff, Copy, Mail, FileText, Upload, CheckCircle2, XCircle, AlertTriangle, Send, Download, RefreshCw, Sparkles, Trash2
} from "lucide-react";

const STAGE_LABELS = {
  offer_drafted: "Offer Drafted",
  offer_sent: "Offer Sent",
  candidate_filling: "Candidate Filling",
  counter_offered: "Counter Offered",
  submitted: "Submitted for Review",
  verified: "Verified",
  contract_sent: "Contract Sent",
  contract_confirmed: "Contract Confirmed",
  enrolled: "Enrolled",
  out_of_budget: "Out of Budget",
};

const fmtINR = (n) => "₹ " + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function Onboarding({ embedded = false } = {}) {
  const { user } = useAuth();
  const [viewMode] = useGlobalViewMode();
  const isMaster = user?.role === "master_admin";
  const [rows, setRows] = useState([]);
  const [viewing, setViewing] = useState(null);
  const load = async () => {
    try {
      const { data } = await api.get("/onboardings");
      setRows(data || []);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="onboarding-page">
      {!embedded && (
        <PageHeader
          moduleKey="hr"
          tone="blue"
          icon={Sparkles}
          eyebrow="HR Module"
          title="Onboarding"
          subtitle="Walk new hires through offer → profile submission → verification → contract → enrolment."
        />
      )}

      <div className="pk-card overflow-hidden">
        {viewMode === "card" ? (
          rows.length === 0 ? (
            <div className="p-12 text-center text-slate-400 text-sm">No candidates in onboarding yet.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-3" data-testid="onb-cards">
              {rows.map((o) => (
                <div key={o.id} className="pk-card p-4 hover:shadow-md transition-shadow" data-testid={`onb-card-${o.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-slate-900 flex items-center gap-1.5 flex-wrap">
                        {o.temp_employee_id && <Badge className="bg-violet-100 text-violet-800 mono text-[9.5px]">{o.temp_employee_id}</Badge>}
                        <span className="truncate">{o.interviewee_name}</span>
                      </div>
                      <div className="text-[10.5px] mono text-slate-500 truncate">{o.personal_email}</div>
                    </div>
                    <StageBadge stage={o.stage} />
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[11.5px] mb-2">
                    <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Position</div><div className="text-slate-800 truncate">{o.position || "—"}</div></div>
                    <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Salary</div><div className="mono text-slate-800">{fmtINR(o.offered_salary)}{o.offer_final && <Badge className="ml-1 bg-rose-100 text-rose-800 text-[9px]">FINAL</Badge>}</div></div>
                    <div className="col-span-2"><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Joining</div><div className="mono text-slate-800">{fmtDateStr(o.joining_date, "—")}</div></div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 pt-2 border-t border-slate-100">
                    <Button size="sm" variant="outline" onClick={() => setViewing(o)} className="h-7 text-[10.5px]" data-testid={`onb-open-${o.id}`}>
                      <Eye size={11} className="mr-1" /> Open
                    </Button>
                    {isMaster && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          if (!window.confirm(`Delete onboarding for "${o.interviewee_name}"?`)) return;
                          try {
                            await api.delete(`/onboardings/${o.id}`);
                            toast.success("Onboarding deleted");
                            load();
                          } catch (e) {
                            toast.error(formatError(e?.response?.data?.detail) || "Could not delete");
                          }
                        }}
                        className="h-7 text-[10.5px] text-rose-600 border-rose-200 hover:bg-rose-50"
                        data-testid={`onb-delete-${o.id}`}
                      >
                        <Trash2 size={11} />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/60 border-b border-slate-100">
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 font-semibold">Candidate</th>
                <th className="px-4 py-3 font-semibold">Position</th>
                <th className="px-4 py-3 font-semibold">Salary</th>
                <th className="px-4 py-3 font-semibold">Joining</th>
                <th className="px-4 py-3 font-semibold">Stage</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-slate-400 text-sm">No candidates in onboarding yet.</td></tr>
              )}
              {rows.map((o) => (
                <tr key={o.id} className="border-b border-slate-100" data-testid={`onb-row-${o.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold flex items-center gap-2 flex-wrap">
                      {o.temp_employee_id && (
                        <Badge className="bg-violet-100 text-violet-800 mono text-[10px]" data-testid={`onb-tempid-${o.id}`}>
                          {o.temp_employee_id}
                        </Badge>
                      )}
                      <span>{o.interviewee_name}</span>
                    </div>
                    <div className="text-[10.5px] text-slate-500 mono">{o.personal_email}</div>
                  </td>
                  <td className="px-4 py-3 text-[12.5px]">{o.position || "—"}</td>
                  <td className="px-4 py-3 mono text-[12.5px]">{fmtINR(o.offered_salary)}{o.offer_final && <Badge className="ml-1 bg-rose-100 text-rose-800 text-[9px]">FINAL</Badge>}</td>
                  <td className="px-4 py-3 mono text-[12.5px]">{fmtDateStr(o.joining_date, "—")}</td>
                  <td className="px-4 py-3"><StageBadge stage={o.stage} /></td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1.5 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => setViewing(o)} className="h-8 text-[11px]" data-testid={`onb-open-${o.id}`}>
                        <Eye size={12} className="mr-1" /> Open
                      </Button>
                      {isMaster && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            if (!window.confirm(`Delete onboarding for "${o.interviewee_name}"?\n\nTemp credentials will be revoked and the candidate will be reverted to the interview list.`)) return;
                            try {
                              await api.delete(`/onboardings/${o.id}`);
                              toast.success("Onboarding deleted");
                              load();
                            } catch (e) {
                              toast.error(formatError(e?.response?.data?.detail) || "Could not delete");
                            }
                          }}
                          className="h-8 text-[11px] text-rose-600"
                          data-testid={`onb-delete-${o.id}`}
                        >
                          <Trash2 size={12} className="mr-1" /> Delete
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {viewing && (
        <OnboardingDetail
          oid={viewing.id}
          onClose={() => setViewing(null)}
          onReload={load}
        />
      )}
    </div>
  );
}

function StageBadge({ stage }) {
  const cls = {
    offer_drafted: "bg-slate-100 text-slate-700",
    offer_sent: "bg-sky-100 text-sky-800",
    candidate_filling: "bg-violet-100 text-violet-800",
    counter_offered: "bg-amber-100 text-amber-800",
    submitted: "bg-blue-100 text-blue-800",
    verified: "bg-emerald-100 text-emerald-800",
    contract_sent: "bg-cyan-100 text-cyan-800",
    contract_confirmed: "bg-teal-100 text-teal-800",
    enrolled: "bg-green-200 text-green-900",
    out_of_budget: "bg-rose-200 text-rose-900",
  }[stage] || "bg-slate-100 text-slate-700";
  return <Badge className={cls}>{STAGE_LABELS[stage] || stage}</Badge>;
}

// ============== Detail Dialog with all 5 steps ==============
function OnboardingDetail({ oid, onClose, onReload }) {
  const [d, setD] = useState(null);
  const [revealPw, setRevealPw] = useState(false);
  const refresh = async () => {
    try {
      const { data } = await api.get(`/onboardings/${oid}`);
      setD(data);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { refresh(); }, [oid]); // eslint-disable-line

  if (!d) return null;

  const stages = ["offer_drafted", "offer_sent", "candidate_filling", "submitted", "verified", "contract_sent", "contract_confirmed", "enrolled"];
  const stageIdx = stages.indexOf(d.stage);

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="onb-detail">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2 flex-wrap">
            {d.temp_employee_id && (
              <Badge className="bg-violet-100 text-violet-800 mono text-[10px] font-bold" data-testid="onb-detail-tempid">
                {d.temp_employee_id}
              </Badge>
            )}
            {d.interviewee_name} <StageBadge stage={d.stage} />
          </DialogTitle>
          <div className="text-[12px] text-slate-600">
            {d.position} · {d.company || "—"} · {d.personal_email} · {fmtINR(d.offered_salary)}/mo
          </div>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-1 flex-wrap text-[10px] font-semibold uppercase tracking-wider">
          {stages.map((s, i) => (
            <span key={s} className={`px-2 py-1 rounded-full border ${i <= stageIdx ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-400 border-slate-200"}`}>
              {i + 1}. {STAGE_LABELS[s]}
            </span>
          ))}
        </div>

        <Tabs defaultValue="step1">
          <TabsList className="grid grid-cols-5">
            <TabsTrigger value="step1" data-testid="onb-tab-step1">1 · Offer</TabsTrigger>
            <TabsTrigger value="step2" data-testid="onb-tab-step2">2 · Candidate Form</TabsTrigger>
            <TabsTrigger value="step3" data-testid="onb-tab-step3">3 · Verify</TabsTrigger>
            <TabsTrigger value="step4" data-testid="onb-tab-step4">4 · Contract</TabsTrigger>
            <TabsTrigger value="step5" data-testid="onb-tab-step5">5 · Enrol</TabsTrigger>
          </TabsList>

          {/* Step 1 — Offer */}
          <TabsContent value="step1" className="space-y-3 pt-3">
            <h3 className="heading text-lg font-bold">Step 1 — Send the Offer</h3>
            <OfferSalaryEditor d={d} oid={oid} refresh={refresh} />

            {/* Candidate accepted — confirmation banner */}
            {d.salary_locked && d.candidate_accepted_salary > 0 && (
              <div className="rounded-md border-2 border-emerald-300 bg-emerald-50 p-4 flex items-center justify-between flex-wrap gap-3" data-testid="onb-accepted-banner">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={18} className="text-emerald-700" />
                  <div>
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-800">
                      Candidate Accepted the Offer
                    </div>
                    <div className="text-[14px] text-slate-800 mt-0.5">
                      Monthly Salary locked at{" "}
                      <b className="mono text-emerald-700" data-testid="onb-accepted-salary">
                        {fmtINR(d.candidate_accepted_salary)}
                      </b>
                      {d.candidate_decision_at && (
                        <span className="text-[11px] text-slate-500 ml-2">
                          · {new Date(d.candidate_decision_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                        </span>
                      )}
                    </div>
                    <div className="text-[10.5px] text-slate-500 italic mt-0.5">
                      This salary will be set as the new employee&rsquo;s Monthly Salary on enrolment.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Reopen Offer — Master Admin only, when stage is out_of_budget */}
            {d.stage === "out_of_budget" && (
              <ReopenOfferAction oid={oid} d={d} refresh={refresh} onReload={onReload} />
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
                <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Temporary Login Credentials</div>
                {d.temp_email ? (
                  <>
                    <div className="text-[12.5px] mono"><b>Email:</b> {d.temp_email}</div>
                    <div className="text-[12.5px] mono flex items-center gap-2">
                      <b>Password:</b>
                      <span className="text-rose-700 font-bold">
                        {revealPw ? d.temp_password_plain : "•".repeat((d.temp_password_plain || "").length)}
                      </span>
                      <button onClick={() => setRevealPw(!revealPw)} className="text-violet-700" data-testid="onb-reveal-pw">
                        {revealPw ? <EyeOff size={12} /> : <Eye size={12} />}
                      </button>
                      <button onClick={() => navigator.clipboard.writeText(d.temp_password_plain).then(() => toast.success("Copied"))} className="text-[10px] text-violet-700 hover:underline">copy</button>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">Expires: {fmtDateStr(d.temp_access_expires_at)}</div>
                  </>
                ) : (
                  <div className="text-[12px] text-slate-500 italic">Not generated yet</div>
                )}
                <Button
                  size="sm" variant="outline"
                  onClick={async () => {
                    try {
                      const { data } = await api.post(`/onboardings/${oid}/generate-credentials`);
                      toast.success("Credentials generated");
                      navigator.clipboard.writeText(`Email: ${data.temp_email}\nPassword: ${data.temp_password}`);
                      refresh(); onReload();
                    } catch (e) { toast.error(formatError(e)); }
                  }}
                  className="h-8 text-[11px] mt-2"
                  data-testid="onb-gen-creds"
                >
                  <KeyRound size={11} className="mr-1" /> {d.temp_email ? "Regenerate" : "Generate Credentials"}
                </Button>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
                <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Quick Actions</div>
                <FinalOfferButton oid={oid} d={d} refresh={refresh} />
                {d.stage === "counter_offered" && (
                  <CounterOfferActions oid={oid} d={d} refresh={refresh} />
                )}
              </div>
            </div>

            <OfferLetterDraft d={d} oid={oid} refresh={refresh} />
          </TabsContent>

          {/* Step 2 — Candidate Form */}
          <TabsContent value="step2" className="space-y-3 pt-3">
            <h3 className="heading text-lg font-bold">Step 2 — Candidate's Submitted Information</h3>
            {d.stage === "offer_sent" || d.stage === "counter_offered" ? (
              <div className="text-slate-500 italic text-[12.5px]">
                Waiting for candidate to log in and respond to the offer.
              </div>
            ) : (
              <CandidateFormReview d={d} oid={oid} refresh={refresh} />
            )}
          </TabsContent>

          {/* Step 3 — Verify */}
          <TabsContent value="step3" className="space-y-3 pt-3">
            <h3 className="heading text-lg font-bold">Step 3 — Verify Each Field</h3>
            <FieldVerification d={d} oid={oid} refresh={refresh} />
          </TabsContent>

          {/* Step 4 — Contract */}
          <TabsContent value="step4" className="space-y-3 pt-3">
            <h3 className="heading text-lg font-bold">Step 4 — Send Contract</h3>
            <CompanyAssignment d={d} oid={oid} refresh={refresh} />
            <EmlUpload d={d} oid={oid} refresh={refresh} />
          </TabsContent>

          {/* Step 5 — Enrol */}
          <TabsContent value="step5" className="space-y-3 pt-3">
            <h3 className="heading text-lg font-bold">Step 5 — Finalize Onboarding</h3>
            <FinalizeOnboarding d={d} oid={oid} refresh={refresh} onReload={onReload} />
          </TabsContent>
        </Tabs>

        {/* Log */}
        <details className="border-t border-slate-100 pt-2">
          <summary className="text-[11px] text-slate-500 cursor-pointer">Activity Log ({(d.log || []).length})</summary>
          <ul className="text-[11.5px] mt-2 space-y-1">
            {(d.log || []).slice().reverse().map((l, i) => (
              <li key={i} className="border-l-2 border-slate-200 pl-2"><b>{l.by}</b> <span className="text-slate-500">— {l.at}</span><br />{l.event}</li>
            ))}
          </ul>
        </details>
      </DialogContent>
    </Dialog>
  );
}
function OfferSalaryEditor({ d, oid, refresh }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(String(d.offered_salary || ""));
  const [busy, setBusy] = useState(false);
  // Editable only before the candidate accepts and only when offer is not FINAL.
  const editable =
    !d.offer_final &&
    ["offer_drafted", "offer_sent", "counter_offered"].includes(d.stage);
  const save = async () => {
    const amount = Number(val);
    if (!amount || amount <= 0) {
      toast.error("Enter a positive amount");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/onboardings/${oid}/update-offered-salary`, { offered_salary: amount });
      toast.success("Offered salary updated");
      setEditing(false);
      refresh();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="bg-violet-50/50 border border-violet-200 rounded-md p-3" data-testid="offer-salary-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10.5px] uppercase tracking-wider font-semibold text-violet-700 mb-0.5">
            Offered Monthly Salary
          </div>
          {!editing ? (
            <div className="text-[18px] font-bold mono text-slate-800" data-testid="offer-salary-value">
              {fmtINR(d.offered_salary)}
              {d.offer_final && (
                <Badge className="ml-2 bg-rose-100 text-rose-800 text-[9.5px] align-middle">FINAL</Badge>
              )}
            </div>
          ) : (
            <div className="flex gap-1.5 items-center mt-1">
              <Input
                type="number"
                value={val}
                onChange={(e) => setVal(e.target.value)}
                className="h-9 w-40 mono text-[14px]"
                placeholder="e.g. 55000"
                data-testid="offer-salary-input"
                autoFocus
              />
              <Button size="sm" onClick={save} disabled={busy} className="h-9 bg-violet-600 hover:bg-violet-700" data-testid="offer-salary-save">
                {busy ? "Saving…" : "Save"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setEditing(false); setVal(String(d.offered_salary || "")); }} className="h-9" data-testid="offer-salary-cancel">
                Cancel
              </Button>
            </div>
          )}
        </div>
        {!editing && editable && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setEditing(true)}
            className="h-8 text-[11px] border-violet-300 text-violet-700 hover:bg-violet-100"
            data-testid="offer-salary-edit"
          >
            Override Value
          </Button>
        )}
      </div>
      {d.candidate_counter_offer > 0 && (
        <div className="mt-2 text-[11.5px] text-amber-800">
          <b>Candidate requested:</b>{" "}
          <span className="mono" data-testid="candidate-requested-value">
            {fmtINR(d.candidate_counter_offer)}
          </span>
        </div>
      )}
      {!editable && !editing && (
        <div className="text-[11px] text-slate-500 italic mt-1">
          {d.offer_final
            ? "Offer is locked as FINAL — value cannot be changed."
            : "Salary is locked once the candidate has accepted."}
        </div>
      )}
    </div>
  );
}



function ReopenOfferAction({ oid, d, refresh, onReload }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(String(d.offered_salary || ""));
  const [busy, setBusy] = useState(false);
  if (user?.role !== "master_admin") {
    return (
      <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-[12px] text-rose-800" data-testid="oob-banner">
        <b>Out of Budget</b> — candidate is no longer active. Ask Master Admin to reopen the offer if you want to renegotiate.
      </div>
    );
  }
  const submit = async () => {
    setBusy(true);
    try {
      const body = val && Number(val) > 0 ? { offered_salary: Number(val) } : {};
      await api.post(`/onboardings/${oid}/reopen-offer`, body);
      toast.success("Offer reopened");
      setOpen(false);
      refresh();
      onReload && onReload();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-md border border-rose-200 bg-rose-50 p-3 flex items-center justify-between flex-wrap gap-3" data-testid="oob-banner">
      <div className="text-[12px] text-rose-800">
        <b>Out of Budget</b> — last offered <span className="mono">{fmtINR(d.offered_salary)}</span>
        {d.candidate_counter_offer > 0 && (
          <> · candidate asked <span className="mono">{fmtINR(d.candidate_counter_offer)}</span></>
        )}
      </div>
      <Button
        size="sm"
        onClick={() => { setVal(String(d.offered_salary || "")); setOpen(true); }}
        className="h-8 text-[11px] bg-violet-600 hover:bg-violet-700"
        data-testid="oob-reopen-btn"
      >
        <RefreshCw size={11} className="mr-1" /> Reopen Offer
      </Button>
      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-w-md" data-testid="reopen-offer-dialog">
          <DialogHeader>
            <DialogTitle className="heading flex items-center gap-2">
              <RefreshCw size={16} className="text-violet-600" /> Reopen Offer
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="rounded-md border border-violet-200 bg-violet-50 p-3 text-[12px] text-violet-900">
              The candidate is currently marked <b>Out of Budget</b>. Reopening will:
              <ul className="list-disc list-inside mt-1.5 space-y-0.5 text-[11.5px]">
                <li>Clear the <b>Out of Budget</b> + <b>FINAL</b> flags</li>
                <li>Re-activate their candidate access</li>
                <li>Send a fresh offer at the amount below — they can accept/counter again</li>
              </ul>
            </div>
            <div>
              <Label className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-600">
                Offered Monthly Salary (₹)
              </Label>
              <Input
                type="number"
                value={val}
                onChange={(e) => setVal(e.target.value)}
                placeholder={String(d.offered_salary || "")}
                className="mt-1 h-10 mono text-[14px]"
                autoFocus
                data-testid="reopen-offer-input"
              />
              <div className="text-[10.5px] text-slate-500 mt-1">
                Leave unchanged to reopen at the previous amount of <span className="mono">{fmtINR(d.offered_salary)}</span>.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="reopen-offer-cancel">Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-violet-600 hover:bg-violet-700" data-testid="reopen-offer-save">
              {busy ? "Reopening…" : "Reopen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function FinalOfferButton({ oid, d, refresh }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(String(d.offered_salary || ""));
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const amount = Number(val);
    if (!amount || amount <= 0) {
      toast.error("Enter a positive amount for the FINAL offer");
      return;
    }
    if (!window.confirm(
      `Make ₹${amount.toLocaleString("en-IN")} the FINAL offer? ` +
      `The candidate will only be able to Accept or Reject — no further negotiation will be allowed.`
    )) return;
    setBusy(true);
    try {
      await api.post(`/onboardings/${oid}/mark-offer-final`, { offered_salary: amount });
      toast.success("Offer marked FINAL");
      setOpen(false);
      refresh();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };
  if (d.offer_final) {
    return (
      <Button
        size="sm"
        variant="outline"
        disabled
        className="h-8 text-[11px] w-full justify-start text-rose-700 border-rose-300"
        data-testid="onb-mark-final"
      >
        <AlertTriangle size={11} className="mr-1" /> Offer is FINAL @ {fmtINR(d.offered_salary)}
      </Button>
    );
  }
  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => { setVal(String(d.offered_salary || "")); setOpen(true); }}
        className="h-8 text-[11px] w-full justify-start"
        data-testid="onb-mark-final"
      >
        <AlertTriangle size={11} className="mr-1" /> Mark Offer as FINAL (no further negotiation)
      </Button>
      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-w-md" data-testid="final-offer-dialog">
          <DialogHeader>
            <DialogTitle className="heading flex items-center gap-2">
              <AlertTriangle size={16} className="text-rose-600" /> Set FINAL Offer
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900">
              <b>Heads up:</b> Once you save this, the candidate will see this as the FINAL offer.
              They can only <b>Accept</b> or <b>Reject</b> — counter-offers will be disabled.
              If they reject, their access is automatically revoked and they are marked
              <i> Out of Budget</i>.
            </div>
            <div>
              <Label className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-600">
                FINAL Monthly Salary (₹) *
              </Label>
              <Input
                type="number"
                value={val}
                onChange={(e) => setVal(e.target.value)}
                placeholder="e.g. 60000"
                className="mt-1 h-10 mono text-[14px]"
                autoFocus
                data-testid="final-offer-input"
              />
              <div className="text-[10.5px] text-slate-500 mt-1">
                Currently offered: <span className="mono">{fmtINR(d.offered_salary)}</span>
                {d.candidate_counter_offer > 0 && (
                  <> · Candidate requested: <span className="mono">{fmtINR(d.candidate_counter_offer)}</span></>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="final-offer-cancel">Cancel</Button>
            <Button
              onClick={submit}
              disabled={busy}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="final-offer-save"
            >
              {busy ? "Saving…" : "Mark as FINAL"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


function CounterOfferActions({ oid, d, refresh }) {
  // "mode" controls which sub-form is visible: '' (action buttons), 'accept', or 'counter'
  const [mode, setMode] = useState("");
  const [val, setVal] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const openAccept = () => {
    setMode("accept");
    setVal(String(d.candidate_counter_offer || ""));
  };
  const openCounter = () => {
    setMode("counter");
    // Pre-fill midway between previously offered and candidate ask, rounded to nearest 1000
    const prev = Number(d.offered_salary || 0);
    const ask = Number(d.candidate_counter_offer || 0);
    const mid = prev && ask ? Math.round((prev + ask) / 2 / 1000) * 1000 : prev || ask || "";
    setVal(String(mid || ""));
    setNote("");
  };

  const sendCounter = async () => {
    const amount = Number(val);
    if (!amount || amount <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      await api.post(`/onboardings/${oid}/hr-counter-offer`, {
        offered_salary: amount,
        note: note.trim(),
      });
      toast.success(`Counter sent @ ${fmtINR(amount)}`);
      setMode("");
      setVal("");
      setNote("");
      refresh();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const acceptCandidateAsk = async () => {
    const amount = Number(val);
    if (!amount || amount <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      await api.post(`/onboardings/${oid}/accept-counter-offer`, { offered_salary: amount });
      toast.success("Accepted");
      setMode("");
      refresh();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 border-t pt-2 border-slate-200" data-testid="counter-offer-block">
      <div className="text-[11.5px] text-amber-800 mb-2">
        <b>Candidate requested:</b>{" "}
        <span className="mono" data-testid="candidate-asked-inline">{fmtINR(d.candidate_counter_offer)}</span>
        {" · "}
        <span className="text-slate-600">Currently offered: </span>
        <span className="mono">{fmtINR(d.offered_salary)}</span>
      </div>

      {mode === "" && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
          <Button
            size="sm"
            onClick={openAccept}
            className="h-7 text-[10.5px] bg-emerald-600 hover:bg-emerald-700"
            data-testid="co-accept-btn"
          >
            Accept Counter
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={openCounter}
            className="h-7 text-[10.5px] border-violet-300 text-violet-700 hover:bg-violet-50"
            data-testid="co-counter-btn"
          >
            Send Counter Offer
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              if (!window.confirm("Mark candidate as Out of Budget? This will revoke their access.")) return;
              await api.post(`/onboardings/${oid}/reject-as-out-of-budget`);
              toast.success("Marked out of budget");
              refresh();
            }}
            className="h-7 text-[10.5px] text-rose-700 border-rose-300 hover:bg-rose-50"
            data-testid="co-reject-btn"
          >
            Reject (OOB)
          </Button>
        </div>
      )}

      {mode === "accept" && (
        <div className="space-y-1.5" data-testid="co-accept-form">
          <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">
            Confirm Accepted Salary (₹)
          </Label>
          <div className="flex gap-1.5">
            <Input
              type="number"
              value={val}
              onChange={(e) => setVal(e.target.value)}
              placeholder="e.g. 75000"
              className="h-8 text-[11.5px] mono"
              autoFocus
              data-testid="co-accept-input"
            />
            <Button size="sm" onClick={acceptCandidateAsk} disabled={busy} className="h-8 text-[11px] bg-emerald-600 hover:bg-emerald-700" data-testid="co-accept-save">
              {busy ? "…" : "Accept"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setMode("")} className="h-8 text-[11px]">Cancel</Button>
          </div>
        </div>
      )}

      {mode === "counter" && (
        <div className="space-y-1.5" data-testid="co-counter-form">
          <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">
            Your Counter Offer (₹)
          </Label>
          <Input
            type="number"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder="e.g. 65000"
            className="h-8 text-[11.5px] mono"
            autoFocus
            data-testid="co-counter-input"
          />
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note for the audit log…"
            className="h-8 text-[11.5px]"
            data-testid="co-counter-note"
          />
          <div className="flex gap-1.5">
            <Button size="sm" onClick={sendCounter} disabled={busy} className="h-8 text-[11px] bg-violet-600 hover:bg-violet-700 flex-1" data-testid="co-counter-send">
              {busy ? "Sending…" : "Send to Candidate"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setMode("")} className="h-8 text-[11px]">Cancel</Button>
          </div>
          <div className="text-[10.5px] text-slate-500 italic">
            The candidate stays logged in and will see this new amount the moment they refresh. You can keep negotiating until you click <b>Mark Offer as FINAL</b>.
          </div>
        </div>
      )}
    </div>
  );
}

function OfferLetterDraft({ d, oid, refresh }) {
  const [busy, setBusy] = useState(false);
  const subject = `Offer letter for the Position — ${d.position || "[Position]"}`;
  const body = `Dear ${d.interviewee_name},

Greetings from Panchakosh Ventures - A Group of Company.

We are pleased to inform you that you have successfully cleared the interview process, and we are delighted to welcome you to Our family.

Temp Employee ID: ${d.temp_employee_id || "[Not yet assigned]"}
Your Date of Joining (DOJ) is: ${fmtDateStr(d.joining_date, "[Joining Date]")}

To complete your onboarding formalities, please submit the following documents before your joining date:

Required Documents
- Aadhaar Card (Identity & Address Proof)
- PAN Card
- Academic Certificates and Mark Sheets
- Previous Employment Records (if applicable)
- Bank Account Proof (Cancelled Cheque / Passbook Copy)
- Passport Size Photograph
- Any other relevant supporting documents

Please email the above documents to: MyHr@techknowskola.com
Subject line: Acceptance to Offer Letter for ${d.interviewee_name} & Temp Employee ID ${d.temp_employee_id || "[Temp ID]"}

Employee Portal Registration

Please complete your profile on our Employee Management Portal:

Website: www.techknowskola.in

Temporary Login Credentials
Login ID: ${d.temp_email || "[Auto Generated after credentials are issued]"}
Password: ${d.temp_password_plain || "[Auto Generated after credentials are issued]"}

To view your Offered Salary, log into the Portal.

Kindly log in and complete all required information. This will help us ensure a smooth and hassle-free onboarding process.

Should you have any questions or require assistance, please feel free to contact the HR Department.

We look forward to having you as part of our team and wish you a successful journey with Us.

Warm Regards,
HR Department`;

  const markSent = async () => {
    if (!oid) return;
    if (!window.confirm("Mark the offer letter as emailed to the candidate?")) return;
    setBusy(true);
    try {
      await api.post(`/onboardings/${oid}/mark-offer-sent`);
      toast.success("Offer letter marked as sent");
      refresh && refresh();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-md border border-violet-200 bg-violet-50/40 p-3" data-testid="offer-letter-draft">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-[10.5px] uppercase tracking-wider font-semibold text-violet-700">Draft Offer Letter</div>
          {d.offer_letter_sent_at && (
            <Badge className="bg-emerald-100 text-emerald-800 text-[10px]" data-testid="offer-letter-sent-badge">
              <CheckCircle2 size={10} className="mr-1" />
              Sent · {new Date(d.offer_letter_sent_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
              {d.offer_letter_sent_by ? ` · ${d.offer_letter_sent_by}` : ""}
            </Badge>
          )}
        </div>
        <div className="flex gap-1 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(d.personal_email || "").then(() => toast.success("Email copied"))} className="h-7 text-[10.5px]" data-testid="copy-email">
            <Copy size={11} className="mr-1" /> Copy Email
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(subject).then(() => toast.success("Subject copied"))} className="h-7 text-[10.5px]" data-testid="copy-subject">
            <Copy size={11} className="mr-1" /> Copy Subject
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(body).then(() => toast.success("Body copied"))} className="h-7 text-[10.5px]" data-testid="copy-body">
            <Copy size={11} className="mr-1" /> Copy Body
          </Button>
          <Button size="sm" variant="outline" onClick={() => {
            const url = `mailto:${d.personal_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
            window.location.href = url;
          }} className="h-7 text-[10.5px]" data-testid="open-mailto">
            <Mail size={11} className="mr-1" /> Open in Email
          </Button>
          <Button
            size="sm"
            onClick={markSent}
            disabled={busy}
            className={`h-7 text-[10.5px] ${d.offer_letter_sent_at ? "bg-slate-500 hover:bg-slate-600" : "bg-emerald-600 hover:bg-emerald-700"}`}
            data-testid="mark-offer-sent"
          >
            <Send size={11} className="mr-1" /> {d.offer_letter_sent_at ? "Re-confirm Sent" : "Email Sent"}
          </Button>
        </div>
      </div>
      <div className="text-[12px] font-semibold text-slate-700 mb-1.5">
        To: <span className="mono">{d.personal_email}</span>
      </div>
      <div className="text-[12px] font-semibold text-slate-700 mb-1.5">Subject: <span className="font-normal">{subject}</span></div>
      <pre className="text-[11.5px] bg-white border border-slate-200 rounded p-3 whitespace-pre-wrap font-sans max-h-[300px] overflow-y-auto" data-testid="offer-body-preview">{body}</pre>
    </div>
  );
}

function CandidateFormReview({ d, oid, refresh }) {
  const f = d.candidate_form || {};
  const docs = d.candidate_documents || [];
  const photoUrl = f.photo_gridfs_id ? `${process.env.REACT_APP_BACKEND_URL}/api/onboardings/${oid}/candidate-photo` : "";

  const submitted = ["submitted", "verified", "contract_sent", "contract_confirmed", "enrolled"].includes(d.stage);

  return (
    <div className="space-y-3">
      {!submitted ? (
        <div className="text-slate-500 italic text-[12.5px]">Candidate has not submitted yet.</div>
      ) : (
        <>
          <div className="flex items-start gap-4 flex-wrap">
            {photoUrl && <img src={photoUrl} alt="" className="w-24 h-24 rounded-md object-cover border" />}
            <div className="flex-1 grid grid-cols-2 md:grid-cols-3 gap-2 text-[12px]">
              {Object.entries(f).filter(([k]) => k !== "photo_gridfs_id" && k !== "photo_uploaded_at").map(([k, v]) => (
                <div key={k}>
                  <div className="text-[9.5px] uppercase text-slate-500 font-semibold">{k.replace(/_/g, " ")}</div>
                  <div className="text-slate-800 mono">{String(v ?? "—") || "—"}</div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Uploaded Documents ({docs.length})</div>
            <ul className="space-y-1">
              {docs.map((doc) => (
                <li key={doc.id} className="flex items-center justify-between border border-slate-200 rounded p-2">
                  <div>
                    <div className="text-[12px] font-semibold">{doc.doc_type}</div>
                    <div className="text-[10.5px] text-slate-500 mono">{doc.filename}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/onboardings/${oid}/document/${doc.id}`, "_blank")} className="h-7 text-[10.5px]">
                    <Download size={11} className="mr-1" /> Open
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

function FieldVerification({ d, oid, refresh }) {
  const f = d.candidate_form || {};
  const v = d.field_verifications || {};
  const fields = ["father_name", "gender", "marital_status", "permanent_address", "blood_group", "pan_number", "aadhaar_number", "date_of_birth", "personal_whatsapp", "account_number", "ifsc_code", "emergency_contact_name", "emergency_relation", "emergency_contact_number"];
  const allOk = fields.every((k) => v[k]?.approved);

  const setVer = async (field, approved, remark = "") => {
    try {
      await api.post(`/onboardings/${oid}/verify-field`, { field, approved, remark });
      refresh();
    } catch (e) { toast.error(formatError(e)); }
  };

  const reopen = async () => {
    const remark = window.prompt("What corrections does the candidate need to make?");
    if (!remark) return;
    try {
      await api.post(`/onboardings/${oid}/reopen-for-resubmit`, { remark });
      toast.success("Re-opened for candidate");
      refresh();
    } catch (e) { toast.error(formatError(e)); }
  };

  const markVerified = async () => {
    try {
      await api.post(`/onboardings/${oid}/mark-verified`);
      toast.success("Marked verified — proceed to Step 4");
      refresh();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div>
      <ul className="space-y-1">
        {fields.map((k) => {
          const val = f[k] ?? "";
          const vs = v[k];
          return (
            <li key={k} className="flex items-center justify-between border border-slate-200 rounded p-2 gap-2 flex-wrap">
              <div className="flex-1 min-w-[180px]">
                <div className="text-[9.5px] uppercase text-slate-500 font-semibold">{k.replace(/_/g, " ")}</div>
                <div className="text-[12.5px] mono text-slate-800">{val || "—"}</div>
                {vs?.remark && <div className="text-[10.5px] text-rose-700 italic">Note: {vs.remark}</div>}
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant={vs?.approved ? "default" : "outline"} onClick={() => setVer(k, true)}
                  className={`h-7 text-[10.5px] ${vs?.approved ? "bg-emerald-600 text-white" : ""}`}
                  data-testid={`verify-ok-${k}`}>
                  <CheckCircle2 size={11} className="mr-1" /> OK
                </Button>
                <Button size="sm" variant="outline" onClick={() => {
                  const r = window.prompt("Why is this rejected?");
                  if (r) setVer(k, false, r);
                }} className="h-7 text-[10.5px] text-rose-700" data-testid={`verify-bad-${k}`}>
                  <XCircle size={11} className="mr-1" /> Reject
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
      <div className="flex items-center justify-end gap-2 mt-3">
        <Button size="sm" variant="outline" onClick={reopen} className="h-8 text-[11px]" data-testid="onb-reopen">
          <RefreshCw size={11} className="mr-1" /> Re-open for Candidate
        </Button>
        <Button size="sm" onClick={markVerified} disabled={!allOk || d.stage === "verified" || d.stage === "contract_sent" || d.stage === "contract_confirmed" || d.stage === "enrolled"} className="h-8 text-[11px] bg-emerald-600 hover:bg-emerald-700" data-testid="onb-mark-verified">
          <CheckCircle2 size={11} className="mr-1" /> Mark All Verified
        </Button>
      </div>
    </div>
  );
}

function CompanyAssignment({ d, oid, refresh }) {
  const [companies, setCompanies] = useState([]);
  const [f, setF] = useState({
    employee_code: "", designation: d.position || "", company: d.company || "",
    date_of_joining: d.joining_date || "", company_email: "", company_gmail: "",
    company_phone: "", job_type: "Full Time", emp_status: "Probation", department: "",
    probation_ends: "",
  });
  useEffect(() => { api.get("/companies").then(({ data }) => setCompanies(data || [])); }, []);
  useEffect(() => {
    if (d.company_assignment) setF((s) => ({ ...s, ...d.company_assignment }));
  }, [d]);

  // Auto-calc probation_ends
  useEffect(() => {
    if (f.date_of_joining) {
      try {
        const dt = new Date(f.date_of_joining);
        dt.setMonth(dt.getMonth() + 6);
        const iso = dt.toISOString().slice(0, 10);
        if (iso !== f.probation_ends) setF((s) => ({ ...s, probation_ends: iso }));
      } catch { /* noop */ }
    }
  }, [f.date_of_joining]); // eslint-disable-line

  const save = async () => {
    if (!f.company_email) return toast.error("Company Email is required (used as login ID)");
    try {
      await api.post(`/onboardings/${oid}/company-assignment`, f);
      toast.success("Company details saved");
      refresh();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="rounded-md border border-slate-200 p-3 bg-slate-50/40">
      <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Company Assignment</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Fld label="Employee ID *"><Input value={f.employee_code} onChange={(e) => setF({ ...f, employee_code: e.target.value })} data-testid="ca-empcode" /></Fld>
        <Fld label="Designation"><Input value={f.designation} onChange={(e) => setF({ ...f, designation: e.target.value })} data-testid="ca-designation" /></Fld>
        <Fld label="Company">
          <Select value={f.company} onValueChange={(v) => setF({ ...f, company: v })}>
            <SelectTrigger data-testid="ca-company"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </Fld>
        <Fld label="Date of Joining"><Input type="date" value={f.date_of_joining || ""} onChange={(e) => setF({ ...f, date_of_joining: e.target.value })} data-testid="ca-doj" /></Fld>
        <Fld label="Company Email (Login) *"><Input type="email" value={f.company_email} onChange={(e) => setF({ ...f, company_email: e.target.value })} data-testid="ca-email" /></Fld>
        <Fld label="Company Gmail"><Input type="email" value={f.company_gmail} onChange={(e) => setF({ ...f, company_gmail: e.target.value })} data-testid="ca-gmail" /></Fld>
        <Fld label="Company Phone"><Input value={f.company_phone} onChange={(e) => setF({ ...f, company_phone: e.target.value })} data-testid="ca-phone" /></Fld>
        <Fld label="Job Type">
          <Select value={f.job_type} onValueChange={(v) => setF({ ...f, job_type: v })}>
            <SelectTrigger data-testid="ca-jobtype"><SelectValue /></SelectTrigger>
            <SelectContent>{["Full Time", "Contractual", "Part Time", "Freelancer", "Commission Based", "Intern"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        </Fld>
        <Fld label="EMP Status">
          <Select value={f.emp_status} onValueChange={(v) => setF({ ...f, emp_status: v })}>
            <SelectTrigger data-testid="ca-empstatus"><SelectValue /></SelectTrigger>
            <SelectContent>{["Confirmed", "PIP", "Probation"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        </Fld>
        <Fld label="Probation Ends (auto)"><Input value={fmtDateStr(f.probation_ends)} disabled className="mono bg-slate-50" /></Fld>
        <Fld label="Department"><Input value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} /></Fld>
      </div>
      <div className="flex justify-end mt-2">
        <Button size="sm" onClick={save} className="h-8 text-[11px] bg-violet-600 hover:bg-violet-700" data-testid="ca-save">Save Company Details</Button>
      </div>
    </div>
  );
}

function EmlUpload({ d, oid, refresh }) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="rounded-md border border-cyan-200 bg-cyan-50/40 p-3">
      <div className="text-[10.5px] uppercase tracking-wider font-semibold text-cyan-700 mb-2">Step 4 — Contract Confirmation</div>
      <div className="text-[12px] text-slate-700 mb-2">
        Send the contract to the candidate via Email externally. After they confirm, download the email
        as <b>.eml</b> and upload it here as the audit record.
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <label className="inline-flex items-center cursor-pointer">
          <Button asChild size="sm" disabled={busy} className="bg-violet-600 hover:bg-violet-700 h-8 text-[11px]">
            <span><Upload size={11} className="mr-1" /> Upload .eml</span>
          </Button>
          <input type="file" accept=".eml,message/rfc822" className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0]; e.target.value = "";
              if (!file) return;
              setBusy(true);
              try {
                const fd = new FormData(); fd.append("file", file);
                await api.post(`/onboardings/${oid}/contract-eml`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                toast.success("Contract uploaded");
                refresh();
              } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
            }} data-testid="onb-eml-input" />
        </label>
        {d.contract_eml_filename && (
          <>
            <Badge className="bg-emerald-100 text-emerald-800">{d.contract_eml_filename}</Badge>
            <Button size="sm" variant="outline" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/onboardings/${oid}/contract-eml`, "_blank")} className="h-8 text-[11px]" data-testid="onb-eml-download">
              <Download size={11} className="mr-1" /> Download
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function FinalizeOnboarding({ d, oid, refresh, onReload }) {
  const ready = d.stage === "contract_confirmed";
  const enrolled = d.stage === "enrolled";

  if (enrolled) {
    return (
      <div className="rounded-md border border-emerald-300 bg-emerald-50 p-4">
        <div className="flex items-center gap-2 text-emerald-800 font-semibold">
          <CheckCircle2 size={16} /> Onboarding Complete
        </div>
        <div className="text-[12px] mt-1.5">
          {d.interviewee_name} is now enrolled. Welcome banner is visible on Employee Directory for 7 days.
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-emerald-200 p-4 bg-emerald-50/40">
      <div className="text-[12.5px] text-slate-700 mb-2">
        Final step — promote candidate to full Employee. The company email becomes their login ID and
        the default password <code>Panch@123</code> is applied. The temporary candidate account is
        disabled. A welcome banner appears on the Employee Directory for the next 7 days.
      </div>
      <Button onClick={async () => {
        if (!window.confirm(`Promote ${d.interviewee_name} to a full employee?`)) return;
        try {
          const { data } = await api.post(`/onboardings/${oid}/finalize-onboarding`);
          toast.success(`Enrolled! Login: ${data.company_email} · Default password: ${data.default_password}`);
          refresh(); onReload();
        } catch (e) { toast.error(formatError(e)); }
      }} disabled={!ready} className="bg-emerald-600 hover:bg-emerald-700" data-testid="onb-finalize">
        {ready ? "Onboard Employee" : "Complete Step 4 first (contract upload)"}
      </Button>
    </div>
  );
}

function Fld({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
